import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { first, map } from 'rxjs/operators';
import { IAnalyticsService } from 'shared-components';

import { AuthenticationDataService } from 'app/core/authentication/services/authentication-data.service';
import { UrlService } from 'app/core/http/url.service';
import { DataTrackLabelEnum, DynamicEventLabels } from '../enums/data-track-label.enum';
import { NewAnalyticsEvent } from '../models/new-analytics-event.model';

import { ServerTimeSyncService } from './server-time-sync-service';

import { environment } from 'env/environment';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class KhAnalyticsService implements IAnalyticsService {
  public events: NewAnalyticsEvent[] = [];

  public constructor(
    private readonly urlService: UrlService,
    private readonly httpClient: HttpClient,
    private readonly serverTimeSyncService: ServerTimeSyncService,
    private readonly authService: AuthenticationDataService
  ) {}

  public trackEventSimple(
    eventType: DataTrackLabelEnum | DynamicEventLabels,
    classId?: string,
    jsonData?: object
  ): void {
    this.trackEvent({ classId, eventType, jsonData });
  }

  public trackEvent(event: NewAnalyticsEvent): void {
    this.trackEventSync(event);
  }

  public storeEvent(event: NewAnalyticsEvent): void {
    this.events.push(event);
    if (this.events.length >= 10) {
      this.flushEvents();
    }
  }

  public flushEvents(): void {
    if (this.events.length > 0) {
      this.trackEvents(this.events);
      this.events = [];
    }
  }

  public trackEvents(events: NewAnalyticsEvent[]): void {
    if (!events || events.length === 0) {
      return;
    }

    const url: string = this.urlService.buildServerApiUrl('Analytics', 'TrackEvents');
    const httpContext: HttpContext = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);

    this.httpClient.post<void>(url, events, { context: httpContext }).pipe(first())
      .subscribe({
        error: err => { console.warn('Analytics trackEvents failed:', err); }
      });
  }

  private trackEventSync(event: NewAnalyticsEvent): void {
    if (!event) {
      return;
    }

    const url: string = this.urlService.buildServerApiUrl('Analytics', 'TrackEvent');
    const httpContext: HttpContext = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);

    this.httpClient.post<void>(url, event, { context: httpContext }).pipe(first())
      .subscribe({
        error: err => { console.warn('Analytics trackEvent failed:', err); }
      });
  }

  // https://stackoverflow.com/a/7616484
  private hashString(str: string): number {
    const lowerStr = str.toLowerCase();

    let hash = 0;
    for (let i = 0; i < lowerStr.length; i++) {
      const char = lowerStr.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0; // Constrain to 32bit integer
    }
    return Math.abs(hash);
  }

  private shouldTrackGSPerfEvent(gameShowRunId?: string): boolean {
    if (!environment.analyticsSampling) {
      return true;
    }
    if (!gameShowRunId) {
      return true;
    }

    const percentage = environment.analyticsSampling.gsPerfPercentage;
    const hash = this.hashString(gameShowRunId);
    const bucket = hash % 100;

    return bucket < percentage;
  }

  public trackGSPerfEvent(
    eventType: DataTrackLabelEnum,
    classId: string,
    gameShowRunId?: string
  ): void {
    if (!this.shouldTrackGSPerfEvent(gameShowRunId)) {
      return;
    }

    const userClaims = this.authService.getUserClaims();
    if (!userClaims || !userClaims.userId) {
      console.warn('Skipping analytics: missing user claims.');
      return;
    }

    this.serverTimeSyncService.getTimeSkew().pipe(
      first(),
      map(result => {
        if (!result) {
          console.warn('Skipping analytics: invalid server time result.');
          return;
        }

        this.trackEvent({
          eventType,
          classId,
          userId: userClaims.userId,
          jsonData: {
            serverTime: result.serverTime,
            clientTime: result.clientTime,
            skew: result.skew,
            averageSkew: result.averageSkew,
            ActivityId: gameShowRunId
          }
        });
      }),
      untilDestroyed(this)
    ).subscribe({
      error: err => {
        console.warn('Skipping analytics due to error in getTimeSkew:', err);
      }
    });
  }
}
